import { FilterOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";
import { ReactNode, useId, useState } from "react";

interface ExpandedWidgetModalProps {
	open: boolean;
	onClose: () => void;
	badge: string;
	title: string;
	/** The applied selection, e.g. "All tenants · Past month". */
	context: string;
	/** Filter fields; the bar adds Apply and Reset. */
	filters: ReactNode;
	onApply: () => void;
	onReset: () => void;
	/** The widget's chart, drawn larger. */
	children: ReactNode;
}

/** A widget's larger view with its own filters; the widget keeps its selection. */
function ExpandedWidgetModal({
	open,
	onClose,
	badge,
	title,
	context,
	filters,
	onApply,
	onReset,
	children,
}: ExpandedWidgetModalProps) {
	const [showFilters, setShowFilters] = useState(false);
	const filtersId = useId();

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			centered
			destroyOnHidden
			width="min(1560px, 84vw)"
			title={
				<div className="dashboard-expanded__title">
					<span className="dashboard-card__badge" aria-hidden="true">
						{badge}
					</span>
					<span>{title}</span>
					<span className="dashboard-expanded__context">{context}</span>
				</div>
			}
		>
			{/* Shown in the header row, but kept out of the title that names the dialog. */}
			<Button
				size="small"
				className="dashboard-expanded__toggle"
				icon={<FilterOutlined />}
				aria-expanded={showFilters}
				aria-controls={filtersId}
				onClick={() => setShowFilters((shown) => !shown)}
			>
				Apply filter
			</Button>
			<div
				id={filtersId}
				className="dashboard-expanded__filters"
				role="group"
				aria-label={`${title} filters`}
				hidden={!showFilters}
			>
				{filters}
				<div className="dashboard-expanded__buttons">
					<Button onClick={onReset}>Reset</Button>
					<Button type="primary" onClick={onApply}>
						Apply
					</Button>
				</div>
			</div>
			<div className="dashboard-expanded__chart">{children}</div>
		</Modal>
	);
}

export default ExpandedWidgetModal;
